import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import FuseDialog from '@fuse/core/FuseDialog';
import { closeDialog } from 'app/store/fuse/dialogSlice';
import DIALOGS from 'app/constants/dialogs';
import { TRANSLATION_KEY } from '../constants';

const CancelSubscriptionDialog = ({ onConfirm, isLoading }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation(TRANSLATION_KEY);
  const [agreed, setAgreed] = useState(false);

  const onClose = useCallback(() => {
    dispatch(closeDialog());
    setTimeout(() => setAgreed(false), 300);
  }, [dispatch]);

  const handleConfirm = () => {
    if (!agreed || isLoading) return;
    onConfirm?.();
  };

  return (
    <FuseDialog
      fullWidth
      maxWidth="sm"
      dialogId={DIALOGS.CANCEL_SUBSCRIPTION}
      onClose={onClose}
      PaperProps={{ sx: { maxWidth: '701px', borderRadius: '16px' } }}
    >
      <Box padding="32px" display="flex" flexDirection="column" gap="24px">
        {/* ── Header ── */}
        <Box display="flex" flexDirection="column" gap="12px">
          <Box display="flex" alignItems="center" gap="10px">
            {/* Cancel icon — red circle with X */}
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: '50%',
                bgcolor: '#dc2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <CloseOutlined sx={{ fontSize: '14px', color: 'white' }} />
            </Box>

            <Typography variant="h5" fontWeight={700} color="accent.900" lineHeight="32px" fontSize="23px">
              {t('CANCEL_SUBSCRIPTION')}
            </Typography>
          </Box>

          {/* Description */}
          <Typography variant="body2" color="accent.500" fontSize="15px" lineHeight="20px" fontWeight={400}>
            {t('CANCEL_SUBSCRIPTION_DESC_1')}
            <br />
            {t('CANCEL_SUBSCRIPTION_DESC_2')}
          </Typography>
        </Box>

        {/* ── Confirmation checkbox ── */}
        <FormControlLabel
          control={
            <Checkbox
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              size="small"
              sx={{
                padding: '1px',
                color: 'grey.400',
                '&.Mui-checked': { color: 'accent.800' },
              }}
            />
          }
          label={
            <Typography
              variant="body2"
              color="accent.900"
              fontSize="13px"
              lineHeight="18px"
              fontWeight={400}
              sx={{ ml: '4px' }}
            >
              {t('CANCEL_UNDERSTAND_LABEL')}
            </Typography>
          }
          sx={{ alignItems: 'flex-start', margin: 0 }}
        />

        {/* ── Action buttons ── */}
        <Box display="flex" gap="12px" alignItems="center">
          <Button
            variant="contained"
            color="secondary"
            disabled={!agreed || isLoading}
            onClick={handleConfirm}
            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              borderRadius: '20px',
              textTransform: 'none',
              px: '16px',
              py: '10px',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            {t('CANCEL_SUBSCRIPTION')}
          </Button>
          <Button
            onClick={onClose}
            sx={{
              textTransform: 'none',
              color: 'accent.900',
              borderRadius: '20px',
              px: '16px',
              py: '10px',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            {t('KEEP_PLAN')}
          </Button>
        </Box>
      </Box>
    </FuseDialog>
  );
};

export default CancelSubscriptionDialog;
